import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/lib/theme";
import { toast } from "sonner";
import { Palette, Upload, Image as ImageIcon, X, Square, Image } from "lucide-react";

// ─────────── Curated font choices (Fontshare + Google) ───────────
const HEADING_FONTS = ["Cabinet Grotesk", "Bricolage Grotesque", "Space Grotesk", "Sentient", "Playfair Display", "Geist", "Switzer", "DM Serif Display", "Manrope", "Inter Tight"];
const BODY_FONTS = ["General Sans", "Inter", "DM Sans", "Manrope", "Switzer", "Geist", "Plus Jakarta Sans", "Outfit", "IBM Plex Sans", "Sentient"];

const TRACKING_OPTIONS = [
  { v: "tighter", label: "Tighter (-0.05em)" },
  { v: "tight", label: "Tight (-0.025em)" },
  { v: "normal", label: "Normal (0)" },
  { v: "wide", label: "Wide (0.025em)" },
  { v: "wider", label: "Wider (0.05em)" },
  { v: "widest", label: "Widest (0.1em)" },
];
const WEIGHT_OPTIONS = [
  { v: 300, label: "300 Light" },
  { v: 400, label: "400 Regular" },
  { v: 500, label: "500 Medium" },
  { v: 600, label: "600 SemiBold" },
  { v: 700, label: "700 Bold" },
  { v: 800, label: "800 ExtraBold" },
  { v: 900, label: "900 Black" },
];

// ─────────── HSL ↔ HEX util ───────────
function hslStrToHex(hsl) {
  if (!hsl) return "#ff5500";
  const m = String(hsl).match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!m) return "#ff5500";
  const h = Number(m[1]) / 360, s = Number(m[2]) / 100, l = Number(m[3]) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * c).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
function hexToHslStr(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      default: hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function AdminBranding() {
  const { refresh } = useTheme();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploadingWide, setUploadingWide] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const wideRef = useRef(null);
  const iconRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/admin/branding");
    setForm(data || {});
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/branding", form);
      await refresh();
      toast.success("Branding updated — applies live across the platform");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const uploadLogo = async (file, kind) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5 MB"); return; }
    const setUploading = kind === "icon" ? setUploadingIcon : setUploadingWide;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(`/admin/branding/logo?kind=${kind}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, ...data }));
      await refresh();
      toast.success(`${kind === "icon" ? "Icon" : "Logo"} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally {
      setUploading(false);
      const ref = kind === "icon" ? iconRef : wideRef;
      if (ref.current) ref.current.value = "";
    }
  };

  const clearLogo = async (kind) => {
    const field = kind === "icon" ? "logo_icon_url" : "logo_url";
    try {
      await api.post("/admin/branding", { ...form, [field]: "" });
      setForm((s) => ({ ...s, [field]: "" }));
      await refresh();
      toast.success("Cleared");
    } catch { toast.error("Could not clear"); }
  };

  const reset = () => {
    setForm({
      platform_name: "Acquilix",
      tagline: "The operating system for growing businesses — project management, HRMS, CRM and ops in one secure workspace.",
      logo_url: null, logo_icon_url: null,
      primary_hsl: "16 100% 50%",
      ring_hsl: "16 100% 50%",
      background_hsl: "0 0% 98%",
      foreground_hsl: "0 0% 9%",
      accent_hsl: "16 100% 96%",
      destructive_hsl: "0 84% 60%",
      radius: "0.25rem",
      font_heading: "Cabinet Grotesk",
      font_body: "General Sans",
      font_scale: 1.0,
      heading_tracking: "tight",
      heading_weight: 700,
      body_weight: 400,
      label_tracking: "widest",
      label_uppercase: true,
      label_weight: 600,
      section_heading_weight: 700,
      nav_label_weight: 500,
    });
  };

  return (
    <>
      <PageHeader
        eyebrow="Theme"
        title="Platform branding"
        description="Change the platform name, palette, fonts, logos and typography. Applies live across marketing, app, and admin."
        testid="admin-branding-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 max-w-5xl">
        <form onSubmit={save} className="grid lg:grid-cols-2 gap-3">
          {/* IDENTITY */}
          <div className="border border-border p-6 space-y-5 lg:col-span-2">
            <div className="label-eyebrow">Identity</div>
            <div className="grid md:grid-cols-2 gap-4">
              <TextField label="Platform name" value={form.platform_name || ""} onChange={set("platform_name")} testid="brand-name-input" />
              <TextField label="Tagline" value={form.tagline || ""} onChange={set("tagline")} testid="brand-tagline-input" />
            </div>
          </div>

          {/* WIDE LOGO */}
          <LogoBlock
            title="Wide logo (header / footer)"
            description="Rendered next to the wordmark on marketing nav, footer and login. Aspect roughly 4:1."
            icon={Image}
            field="logo_url"
            value={form.logo_url}
            inputRef={wideRef}
            uploading={uploadingWide}
            onUpload={(f) => uploadLogo(f, "wide")}
            onClear={() => clearLogo("wide")}
            onUrl={(v) => set("logo_url")(v)}
            testid="brand-logo"
          />

          {/* SQUARE LOGO */}
          <LogoBlock
            title="Square icon (sidebar / favicon)"
            description="Small square mark for the in-app sidebar and tight surfaces where the wide logo gets squashed. Aspect 1:1."
            icon={Square}
            field="logo_icon_url"
            value={form.logo_icon_url}
            inputRef={iconRef}
            uploading={uploadingIcon}
            onUpload={(f) => uploadLogo(f, "icon")}
            onClear={() => clearLogo("icon")}
            onUrl={(v) => set("logo_icon_url")(v)}
            testid="brand-icon"
            square
          />

          {/* PALETTE */}
          <div className="border border-border p-6 space-y-5 lg:col-span-2">
            <div className="label-eyebrow">Color palette</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <ColorField label="Primary" value={form.primary_hsl || "16 100% 50%"} onChange={set("primary_hsl")} testid="brand-color-primary" />
              <ColorField label="Background" value={form.background_hsl || "0 0% 98%"} onChange={set("background_hsl")} testid="brand-color-bg" />
              <ColorField label="Foreground (text)" value={form.foreground_hsl || "0 0% 9%"} onChange={set("foreground_hsl")} testid="brand-color-fg" />
              <ColorField label="Accent (chips / highlights)" value={form.accent_hsl || "16 100% 96%"} onChange={set("accent_hsl")} testid="brand-color-accent" />
              <ColorField label="Destructive (delete / errors)" value={form.destructive_hsl || "0 84% 60%"} onChange={set("destructive_hsl")} testid="brand-color-destructive" />
              <ColorField label="Focus ring" value={form.ring_hsl || form.primary_hsl || "16 100% 50%"} onChange={set("ring_hsl")} testid="brand-color-ring" />
            </div>
            <TextField label="Border radius (e.g. 0.25rem, 0.5rem, 9999px)" value={form.radius || ""} onChange={set("radius")} testid="brand-radius-input" />
          </div>

          {/* Sep 2026 · r-login-magic — rotating hero images + custom
              motivational quotes on the login split-screen. Both fields
              are lists — one per line — and either can be blank. */}
          <div className="border border-border p-6 space-y-5 lg:col-span-2" data-testid="brand-login-block">
            <div className="label-eyebrow">Login page magic</div>
            <p className="text-xs text-muted-foreground -mt-3">
              Rotating hero images + motivational quotes shown on the split-screen login. Each on its own line. Leave blank to use the curated defaults.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Hero image URLs (one per line)</Label>
              <textarea
                value={(form.login_hero_images || []).join("\n")}
                onChange={(e) => set("login_hero_images")(
                  e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)
                )}
                rows={4}
                placeholder="https://images.example.com/team-1.jpg&#10;https://images.example.com/team-2.jpg"
                className="w-full text-sm px-3 py-2 border border-border bg-background font-mono"
                data-testid="brand-login-hero-images"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Motivational quotes (one per line)</Label>
              <textarea
                value={(form.login_quotes || []).join("\n")}
                onChange={(e) => set("login_quotes")(
                  e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)
                )}
                rows={5}
                placeholder={"Small ships steer fast — you're the reason this workspace hums today.\nShip the boring stuff. The magic follows on its own."}
                className="w-full text-sm px-3 py-2 border border-border bg-background"
                data-testid="brand-login-quotes"
              />
            </div>
          </div>

          {/* TYPOGRAPHY */}
          <div className="border border-border p-6 space-y-5 lg:col-span-2">
            <div className="label-eyebrow">Typography</div>
            <div className="grid md:grid-cols-2 gap-4">
              <FontPicker label="Heading font" value={form.font_heading} onChange={set("font_heading")} options={HEADING_FONTS} testid="brand-font-heading" />
              <FontPicker label="Body font" value={form.font_body} onChange={set("font_body")} options={BODY_FONTS} testid="brand-font-body" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <NumberField label="Font scale" min={0.85} max={1.25} step={0.05} value={form.font_scale ?? 1.0} onChange={set("font_scale")} testid="brand-font-scale" />
              <SelectField label="Heading letter-spacing" value={form.heading_tracking || "tight"} onChange={set("heading_tracking")} options={TRACKING_OPTIONS} testid="brand-heading-tracking" />
              <SelectField label="Heading weight" value={String(form.heading_weight ?? 700)} onChange={(v) => set("heading_weight")(Number(v))} options={WEIGHT_OPTIONS.map((o) => ({ v: String(o.v), label: o.label }))} testid="brand-heading-weight" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <SelectField label="Section heading weight" value={String(form.section_heading_weight ?? 700)} onChange={(v) => set("section_heading_weight")(Number(v))} options={WEIGHT_OPTIONS.map((o) => ({ v: String(o.v), label: o.label }))} testid="brand-section-weight" />
              <SelectField label="Body weight" value={String(form.body_weight ?? 400)} onChange={(v) => set("body_weight")(Number(v))} options={WEIGHT_OPTIONS.map((o) => ({ v: String(o.v), label: o.label }))} testid="brand-body-weight" />
              <SelectField label="Nav label weight" value={String(form.nav_label_weight ?? 500)} onChange={(v) => set("nav_label_weight")(Number(v))} options={WEIGHT_OPTIONS.map((o) => ({ v: String(o.v), label: o.label }))} testid="brand-nav-weight" />
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-end">
              <SelectField label="Field-label tracking" value={form.label_tracking || "widest"} onChange={set("label_tracking")} options={TRACKING_OPTIONS} testid="brand-label-tracking" />
              <SelectField label="Field-label weight" value={String(form.label_weight ?? 600)} onChange={(v) => set("label_weight")(Number(v))} options={WEIGHT_OPTIONS.map((o) => ({ v: String(o.v), label: o.label }))} testid="brand-label-weight" />
              <div className="flex items-center justify-between border border-border p-3">
                <div>
                  <Label className="label-eyebrow text-[10px]">Uppercase labels</Label>
                  <p className="text-[10px] text-foreground/60">Renders field labels in ALL CAPS.</p>
                </div>
                <Switch checked={!!form.label_uppercase} onCheckedChange={(v) => set("label_uppercase")(v)} data-testid="brand-label-uppercase" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Fonts are loaded from Fontshare / Google Fonts. You can type any font name available there.</p>
          </div>

          {/* PREVIEW */}
          <div className="lg:col-span-2 border border-border p-6 bg-card space-y-3" data-testid="brand-preview" style={{
            fontFamily: `'${form.font_body || "General Sans"}', sans-serif`,
            background: `hsl(${form.background_hsl || "0 0% 98%"})`,
            color: `hsl(${form.foreground_hsl || "0 0% 9%"})`,
          }}>
            <div className="flex items-center gap-3 mb-3">
              <Palette className="w-5 h-5" style={{ color: `hsl(${form.primary_hsl || "16 100% 50%"})` }} />
              <div className="label-eyebrow" style={{ letterSpacing: trackingToCss(form.label_tracking), textTransform: form.label_uppercase ? "uppercase" : "none", fontWeight: form.label_weight || 600 }}>Preview</div>
            </div>
            <div style={{
              fontFamily: `'${form.font_heading || "Cabinet Grotesk"}', sans-serif`,
              letterSpacing: trackingToCss(form.heading_tracking),
              fontWeight: form.heading_weight || 700,
              fontSize: `${2.2 * (form.font_scale || 1)}rem`,
            }}>
              {form.platform_name || "Acquilix"}
            </div>
            <div style={{ fontWeight: form.body_weight || 400 }}>{form.tagline}</div>
            <div className="flex gap-2 mt-3">
              <button type="button" className="px-4 py-2 text-xs font-bold" style={{ background: `hsl(${form.primary_hsl || "16 100% 50%"})`, color: "white", borderRadius: form.radius || "0.25rem" }}>Primary button</button>
              <span className="px-3 py-1 text-xs font-mono border" style={{ background: `hsl(${form.accent_hsl || "16 100% 96%"})`, borderColor: `hsl(${form.primary_hsl || "16 100% 50%"} / 0.3)` }}>chip</span>
              <span className="px-3 py-1 text-xs font-mono" style={{ color: `hsl(${form.destructive_hsl || "0 84% 60%"})` }}>error text</span>
            </div>
            <div className="flex gap-2 mt-2">
              {["primary_hsl", "background_hsl", "foreground_hsl", "accent_hsl", "destructive_hsl"].map((k) => (
                <div key={k} className="w-9 h-9 border border-border" style={{ background: `hsl(${form[k] || "0 0% 50%"})` }} title={k} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={reset} className="rounded-none" data-testid="brand-reset-btn">Reset to defaults</Button>
            <Button type="submit" disabled={busy} className="rounded-none" data-testid="brand-save-btn">{busy ? "Saving…" : "Save branding"}</Button>
          </div>
        </form>
      </div>
    </>
  );
}

function trackingToCss(t) {
  return ({ tighter: "-0.05em", tight: "-0.025em", normal: "0", wide: "0.025em", wider: "0.05em", widest: "0.1em" })[t] || "0";
}

function LogoBlock({ title, description, icon: Icon, value, inputRef, uploading, onUpload, onClear, onUrl, testid, square }) {
  return (
    <div className="border border-border p-6 space-y-4" data-testid={`${testid}-section`}>
      <div className="label-eyebrow inline-flex items-center gap-2"><Icon className="w-3.5 h-3.5" /> {title}</div>
      <p className="text-xs text-foreground/60">{description}</p>
      <div className="flex items-center gap-4">
        <div className={`${square ? "w-16 h-16" : "w-24 h-12"} border border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0`}>
          {value ? (
            <img src={value} alt="logo" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <ImageIcon className="w-6 h-6 text-foreground/40" />
          )}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex gap-2 flex-wrap">
            <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => onUpload(e.target.files?.[0])} data-testid={`${testid}-file-input`} />
            <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload-btn`}>
              <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
            {value && (
              <Button type="button" variant="ghost" className="rounded-none h-9 text-destructive hover:text-destructive" onClick={onClear} data-testid={`${testid}-clear-btn`}>
                <X className="w-3.5 h-3.5 mr-1.5" /> Clear
              </Button>
            )}
          </div>
          <Input placeholder="…or paste an external URL" value={value || ""} onChange={(e) => onUrl(e.target.value)} className="h-9 text-xs" data-testid={`${testid}-url-input`} />
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  const hex = hslStrToHex(value);
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <div className="flex gap-2 items-stretch">
        <label className="relative w-12 border border-border cursor-pointer shrink-0" style={{ background: `hsl(${value})` }} title="Pick a colour">
          <input type="color" value={hex} onChange={(e) => onChange(hexToHslStr(e.target.value))} className="opacity-0 absolute inset-0 cursor-pointer w-full h-full" data-testid={`${testid}-picker`} />
        </label>
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-10 font-mono text-xs" data-testid={`${testid}-text`} />
      </div>
      <p className="text-[10px] text-foreground/50 mt-1 font-mono">{hex}</p>
    </div>
  );
}

function FontPicker({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <Input list={`${testid}-list`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
      <datalist id={`${testid}-list`}>
        {options.map((f) => <option key={f} value={f} />)}
      </datalist>
    </div>
  );
}

function NumberField({ label, value, onChange, min, max, step, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <Input type="number" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="h-10" data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={String(o.v)} value={String(o.v)}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
